import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api.config';
import './QuoteAcceptPage.css';

interface QuoteInfo {
  bookingId: number;
  quoteNumber: string;
  customerName: string;
  tourDate: string;
  tourTime: string;
  participantCount: number;
  totalPrice: number;
  validUntil: string | null;
  canAccept: boolean;
  statusMessage: string | null;
  requiresLogin: boolean;
}

const QuoteAcceptPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [quoteInfo, setQuoteInfo] = useState<QuoteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState(false);

  const quoteNumber = searchParams.get('quote');

  useEffect(() => {
    const fetchQuoteInfo = async () => {
      if (!bookingId) {
        setError('Keine Buchungs-ID angegeben.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/quote-info`);

        // Check for empty response
        const text = await response.text();
        if (!text) {
          throw new Error('Leere Antwort vom Server.');
        }

        // Parse JSON
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error('Ungültiges Datenformat vom Server.');
        }

        if (!response.ok) {
          throw new Error(data.message || `Fehler: ${response.status}`);
        }

        setQuoteInfo(data);
      } catch (err) {
        console.error('Error fetching quote info:', err);
        setError(err instanceof Error ? err.message : 'Fehler beim Laden des Angebots.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuoteInfo();
  }, [bookingId]);

  const handleAcceptQuote = async () => {
    if (!bookingId || !isAuthenticated) {
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      // Try both token keys for compatibility
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        throw new Error('Sitzung abgelaufen. Bitte erneut anmelden.');
      }

      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/accept-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      // Check for empty response
      const text = await response.text();

      if (!response.ok) {
        let errorMessage = `Fehler: ${response.status}`;
        if (text) {
          try {
            const errorData = JSON.parse(text);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch {
            // Response wasn't JSON
          }
        }
        throw new Error(errorMessage);
      }

      setSuccess(true);

      // Reload quote info to update status
      if (text) {
        try {
          const data = JSON.parse(text);
          if (data.status) {
            setQuoteInfo(prev => prev ? { ...prev, canAccept: false, statusMessage: 'Angebot wurde angenommen.' } : null);
          }
        } catch {
          // Ignore parse errors on success
        }
      }
    } catch (err) {
      console.error('Error accepting quote:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Annehmen des Angebots.');
    } finally {
      setAccepting(false);
    }
  };

  const handleLoginRedirect = () => {
    // Save the current URL to redirect back after login
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    navigate(`/login?returnUrl=${returnUrl}`);
  };

  if (authLoading || loading) {
    return (
      <div className="quote-accept-page">
        <div className="quote-accept-card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Angebot wird geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !quoteInfo) {
    return (
      <div className="quote-accept-page">
        <div className="quote-accept-card error-card">
          <div className="error-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
            </svg>
          </div>
          <h2>Angebot nicht gefunden</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="quote-accept-page">
        <div className="quote-accept-card success-card">
          <div className="success-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
            </svg>
          </div>
          <h2>Angebot angenommen!</h2>
          <p>
            Vielen Dank! Sie haben das Angebot <strong>{quoteInfo?.quoteNumber}</strong> erfolgreich angenommen.
          </p>
          <p className="success-info">
            Ihre Buchung wurde bestätigt. Sie erhalten in Kürze eine Bestätigung per E-Mail.
          </p>
          <div className="success-actions">
            <button onClick={() => navigate('/dashboard')} className="btn-primary">
              Zu meinen Buchungen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quote-accept-page">
      <div className="quote-accept-card">
        <div className="quote-header">
          <img
            src="/logo-frankenfloss.svg"
            alt="Frankenfloss Logo"
            className="quote-logo"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <h1>Angebot annehmen</h1>
        </div>

        {quoteInfo && (
          <>
            <div className="quote-details">
              <h2>Angebotsdetails</h2>
              <div className="quote-info-grid">
                <div className="info-row">
                  <span className="info-label">Angebotsnummer:</span>
                  <span className="info-value">{quoteInfo.quoteNumber || quoteNumber}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Kunde:</span>
                  <span className="info-value">{quoteInfo.customerName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Datum:</span>
                  <span className="info-value">{quoteInfo.tourDate}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Uhrzeit:</span>
                  <span className="info-value">{quoteInfo.tourTime} Uhr</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Teilnehmer:</span>
                  <span className="info-value">{quoteInfo.participantCount} Personen</span>
                </div>
                <div className="info-row highlight">
                  <span className="info-label">Gesamtpreis:</span>
                  <span className="info-value price">{quoteInfo.totalPrice.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                </div>
                {quoteInfo.validUntil && (
                  <div className="info-row">
                    <span className="info-label">Gültig bis:</span>
                    <span className="info-value">{new Date(quoteInfo.validUntil).toLocaleDateString('de-DE')}</span>
                  </div>
                )}
              </div>
            </div>

            {quoteInfo.statusMessage && !quoteInfo.canAccept && (
              <div className="quote-status-message warning">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                </svg>
                <p>{quoteInfo.statusMessage}</p>
              </div>
            )}

            {error && (
              <div className="quote-status-message error">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
                </svg>
                <p>{error}</p>
              </div>
            )}

            <div className="quote-actions">
              {quoteInfo.canAccept ? (
                isAuthenticated ? (
                  <button
                    onClick={handleAcceptQuote}
                    disabled={accepting}
                    className="btn-accept"
                  >
                    {accepting ? (
                      <>
                        <span className="btn-spinner"></span>
                        Wird verarbeitet...
                      </>
                    ) : (
                      'Angebot annehmen'
                    )}
                  </button>
                ) : (
                  <div className="login-required">
                    <p>Bitte melden Sie sich an, um das Angebot anzunehmen.</p>
                    <button onClick={handleLoginRedirect} className="btn-login">
                      Anmelden
                    </button>
                  </div>
                )
              ) : (
                <p className="cannot-accept-info">
                  Dieses Angebot kann nicht mehr angenommen werden.
                </p>
              )}
            </div>

            <div className="quote-footer">
              <p>
                Bei Fragen erreichen Sie uns unter:<br />
                <strong>+49 170 7744661</strong> oder <strong>info@frankenfloss.de</strong>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuoteAcceptPage;
